import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";
import { useWSEvent } from "@/messaging/WSContext";
import type { PresenceStatus } from "@/messaging/types";
import { roleLabel } from "@/types";

// ── types (mirror app/routes/audit.py::UserActivityOut) ───────────────────────
interface ActivityUser {
  id: string;
  name: string | null;
  email: string;
  role: string;
  avatar_url: string | null;
  status: PresenceStatus;
  last_active_at: string | null;
}
interface LoginRow {
  at: string;
  ip: string | null;
  user_agent: string | null;
}
interface Segment {
  status: "active" | "away" | "offline";
  start: string;
  end: string;
}
interface ActivityRow {
  at: string;
  action: string;
  category: string;
  path: string | null;
  method: string | null;
  status_code: number | null;
}
interface ActivityStats {
  active_minutes: number;
  events: number;
  writes: number;
  login_count: number;
  first_at: string | null;
  last_at: string | null;
  top_actions: { action: string; count: number }[];
}
interface Anomaly {
  kind: string;
  label: string;
  tone: "amber" | "rose";
}
interface UserActivity {
  user: ActivityUser;
  day: string;
  source: string;
  logins: LoginRow[];
  segments: Segment[];
  activity: ActivityRow[];
  stats: ActivityStats;
  heatmap: number[][];
  anomalies: Anomaly[];
}

const DAY_MS = 86_400_000;
const TZ = "+04:00"; // Tbilisi, no DST

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "Asia/Tbilisi",
  });
}
function hm(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tbilisi",
  });
}
function dur(min: number): string {
  if (min <= 0) return "0m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function ago(iso: string | null): string {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function catClass(cat: string): string {
  if (cat === "admin") return "bg-rose-500/15 text-rose-300 border-rose-500/30";
  if (cat === "auth") return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (cat === "write") return "bg-sky-500/15 text-sky-300 border-sky-500/30";
  return "bg-white/5 text-slate-400 border-white/10";
}
function shortUA(ua: string | null): string {
  if (!ua) return "unknown device";
  const b = /Edg/.test(ua) ? "Edge" : /Chrome/.test(ua) ? "Chrome" : /Firefox/.test(ua) ? "Firefox" : /Safari/.test(ua) ? "Safari" : "Browser";
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${b} · ${os}` : b;
}

const SEG_COLOR: Record<Segment["status"], string> = {
  active: "bg-emerald-500/80",
  away: "bg-amber-500/70",
  offline: "bg-white/[0.05]",
};
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Last 14 day options (value = "" for today, else YYYY-MM-DD local). */
function dayOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(now.getTime() - i * DAY_MS);
    const iso = d.toLocaleDateString("en-CA", { timeZone: "Asia/Tbilisi" });
    const label =
      i === 0
        ? "Today"
        : i === 1
          ? "Yesterday"
          : d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Tbilisi" });
    out.push({ value: i === 0 ? "" : iso, label });
  }
  return out;
}

export function UserActivity() {
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const [day, setDay] = useState("");
  const [data, setData] = useState<UserActivity | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const days = useMemo(dayOptions, []);

  useEffect(() => {
    let alive = true;
    setState("loading");
    api<UserActivity>(`/api/audit/users/${userId}/activity${day ? `?day=${day}` : ""}`)
      .then((r) => alive && (setData(r), setState("ready")))
      .catch(() => alive && setState("error"));
    return () => {
      alive = false;
    };
  }, [userId, day]);

  useWSEvent("presence.changed", (ev) => {
    if (ev.user_id !== userId) return;
    setData((d) =>
      d ? { ...d, user: { ...d.user, status: ev.status, last_active_at: ev.last_active_at ?? d.user.last_active_at } } : d,
    );
  });

  const isToday = day === "";
  const dayStartMs = useMemo(() => {
    const label = data?.day ?? new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Tbilisi" });
    return new Date(`${label}T00:00:00${TZ}`).getTime();
  }, [data?.day]);
  const nowPct = isToday ? Math.min(100, ((Date.now() - dayStartMs) / DAY_MS) * 100) : 100;

  const name = data?.user.name || data?.user.email || "User";

  return (
    <div className="w-full space-y-5">
      {/* back link + hero */}
      <div>
        <button
          onClick={() => navigate("/audit")}
          className="text-[13px] text-accent-400 hover:text-accent-300 inline-flex items-center gap-1"
        >
          ← back to Audit
        </button>
        <div className="mt-3 flex items-center gap-4">
          {data ? (
            <Avatar
              url={data.user.avatar_url}
              name={data.user.name || data.user.email}
              email={data.user.email}
              size="lg"
              status={data.user.status}
            />
          ) : (
            <Skeleton className="h-16 w-16 rounded-full" />
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-[22px] font-semibold text-slate-100 leading-tight truncate">{name}</h1>
            {data && (
              <div className="text-[13px] text-slate-500 mt-0.5">
                {roleLabel(data.user.role)} · <StatusPill status={data.user.status} />{" "}
                {data.user.status === "active" ? "active now" : `active ${ago(data.user.last_active_at)}`}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* day selector */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-thin pb-1">
        {days.map((d) => (
          <button
            key={d.value || "today"}
            onClick={() => setDay(d.value)}
            className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-[13px] font-medium transition ${
              day === d.value
                ? "bg-accent-500/15 text-accent-200 border border-accent-500/30"
                : "text-slate-400 hover:text-slate-200 border border-white/5"
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {state === "loading" && (
        <div className="space-y-5">
          <Skeleton className="h-24 w-full rounded-xl" />
          <div className="grid gap-5 lg:grid-cols-3">
            <Skeleton className="h-80 w-full rounded-xl lg:col-span-2" />
            <Skeleton className="h-80 w-full rounded-xl" />
          </div>
        </div>
      )}
      {state === "error" && (
        <div className="card p-10 text-center text-sm text-slate-500">Couldn't load activity.</div>
      )}

      {state === "ready" && data && (
        <>
          {/* anomalies */}
          {data.anomalies.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.anomalies.map((a) => (
                <span
                  key={a.kind}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium ${
                    a.tone === "rose"
                      ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-300"
                  }`}
                >
                  ⚠ {a.label}
                </span>
              ))}
            </div>
          )}

          {/* summary stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Active time" value={dur(data.stats.active_minutes)} />
            <StatTile label="Actions" value={String(data.stats.events)} />
            <StatTile label="Writes" value={String(data.stats.writes)} />
            <StatTile label="Logins" value={String(data.stats.login_count)} />
          </div>

          {/* timeline (full width) */}
          <section className="card p-5">
            <SectionLabel>Timeline · {data.day}</SectionLabel>
            <div className="relative mt-3 h-10 w-full overflow-hidden rounded-lg bg-white/[0.05] ring-1 ring-white/5">
              {data.segments.map((s, i) => {
                const left = ((new Date(s.start).getTime() - dayStartMs) / DAY_MS) * 100;
                const width = ((new Date(s.end).getTime() - new Date(s.start).getTime()) / DAY_MS) * 100;
                if (width <= 0 || s.status === "offline") return null;
                return (
                  <div
                    key={i}
                    className={`absolute top-0 h-full ${SEG_COLOR[s.status]}`}
                    style={{ left: `${Math.max(0, left)}%`, width: `${Math.min(100, width)}%` }}
                    title={`${s.status} · ${hm(s.start)}–${hm(s.end)}`}
                  />
                );
              })}
              {data.logins.map((lg, i) => {
                const left = ((new Date(lg.at).getTime() - dayStartMs) / DAY_MS) * 100;
                return (
                  <div
                    key={`lg${i}`}
                    className="absolute top-0 h-full w-0.5 bg-accent-300"
                    style={{ left: `${Math.max(0, Math.min(100, left))}%` }}
                    title={`Logged in ${clock(lg.at)} · ${shortUA(lg.user_agent)}${lg.ip ? ` · ${lg.ip}` : ""}`}
                  />
                );
              })}
              {isToday && <div className="absolute top-0 h-full w-px bg-white/40" style={{ left: `${nowPct}%` }} />}
            </div>
            {/* hour axis */}
            <div className="mt-1.5 flex justify-between text-[10px] text-slate-600 tabular-nums">
              {Array.from({ length: 13 }, (_, i) => i * 2).map((h) => (
                <span key={h}>{String(h).padStart(2, "0")}</span>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-4 text-[12px] text-slate-500">
              <Legend color="bg-emerald-500/80" label="active" />
              <Legend color="bg-amber-500/70" label="away" />
              <Legend color="bg-accent-300" label="login" />
              {data.source === "reconstructed" && (
                <span className="ml-auto text-slate-600" title="No presence history yet — bands inferred from activity">
                  ~ inferred from activity
                </span>
              )}
            </div>
          </section>

          {/* two-column: activity log | side panels */}
          <div className="grid gap-5 lg:grid-cols-3">
            {/* activity log */}
            <section className="card p-5 lg:col-span-2 lg:order-1 order-2">
              <SectionLabel>Activity log</SectionLabel>
              <div className="mt-3 space-y-0.5 max-h-[560px] overflow-y-auto scrollbar-thin">
                {data.activity.length === 0 && (
                  <div className="text-[13px] text-slate-500 py-4">No recorded activity this day.</div>
                )}
                {data.activity.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 text-[13px] py-1 border-b border-white/[0.03] last:border-0">
                    <span className="font-mono tabular-nums text-slate-500 w-[64px] flex-shrink-0">{clock(r.at)}</span>
                    <span className={`mt-px inline-flex rounded border px-1.5 text-[10px] uppercase ${catClass(r.category)}`}>
                      {r.category}
                    </span>
                    <span className="font-mono text-slate-200 flex-shrink-0">{r.action}</span>
                    {r.path && <span className="font-mono text-slate-600 truncate hidden sm:inline">{r.method} {r.path}</span>}
                    {r.status_code != null && r.status_code >= 400 && (
                      <span className="ml-auto text-rose-300 tabular-nums flex-shrink-0">{r.status_code}</span>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* side column */}
            <div className="space-y-5 lg:order-2 order-1">
              {/* logins */}
              <section className="card p-5">
                <SectionLabel>Logins</SectionLabel>
                <div className="mt-3 space-y-2.5">
                  {data.logins.length === 0 && <div className="text-[13px] text-slate-500">No logins this day.</div>}
                  {data.logins.map((lg, i) => (
                    <div key={i} className="text-[13px]">
                      <div className="flex items-center gap-2">
                        <span className="font-mono tabular-nums text-slate-200">{clock(lg.at)}</span>
                        <span className="text-slate-400">{shortUA(lg.user_agent)}</span>
                      </div>
                      {lg.ip && <div className="text-[11px] text-slate-600 font-mono">{lg.ip}</div>}
                    </div>
                  ))}
                </div>
              </section>

              {/* heatmap */}
              <section className="card p-5">
                <SectionLabel>Typical hours (14d)</SectionLabel>
                <Heatmap grid={data.heatmap} />
              </section>
            </div>
          </div>
        </>
      )}

      {data && (
        <div className="text-[11px] text-slate-600">
          <Link to="/audit" className="hover:text-slate-400">
            ← Audit
          </Link>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{children}</div>;
}
function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-sm ${color}`} />
      {label}
    </span>
  );
}
function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-[22px] font-semibold text-slate-100 tabular-nums truncate">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
function StatusPill({ status }: { status: PresenceStatus }) {
  const c =
    status === "active"
      ? "text-emerald-300"
      : status === "away"
        ? "text-amber-300"
        : "text-slate-400";
  return <span className={`font-medium ${c}`}>{status}</span>;
}
function Heatmap({ grid }: { grid: number[][] }) {
  const max = Math.max(1, ...grid.flat());
  return (
    <div className="mt-3 flex gap-2">
      <div className="flex flex-col gap-[3px] pt-4">
        {WEEKDAYS.map((d, i) => (
          <span key={i} className="h-[12px] text-[9px] leading-[12px] text-slate-600">{d}</span>
        ))}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between text-[9px] text-slate-600 mb-1 px-px">
          {[0, 6, 12, 18].map((h) => (
            <span key={h}>{h}:00</span>
          ))}
          <span>23</span>
        </div>
        <div className="flex flex-col gap-[3px]">
          {grid.map((row, ri) => (
            <div key={ri} className="flex gap-[3px]">
              {row.map((n, hi) => {
                const t = n / max;
                const bg =
                  n === 0
                    ? "bg-white/[0.04]"
                    : t > 0.66
                      ? "bg-accent-400"
                      : t > 0.33
                        ? "bg-accent-500/70"
                        : "bg-accent-500/35";
                return <span key={hi} className={`h-[12px] flex-1 rounded-[2px] ${bg}`} title={n ? `${n} events · ${hi}:00` : ""} />;
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
